import { formatTime, rangeLabels, Tag, Tooltip } from "@xola/ui-kit";
import { DateFormat, formatDate, PurchaseItemStatus } from "common";
import dayjs from "dayjs";
import { isEqual, map, omit, startCase } from "lodash";
import React from "react";
import { useSeller } from "../../../hooks/useAccount";
import { getRelativeDateRange } from "../../../models/ReportFilter";
import { Join } from "../../Common/Join";
import { ExperienceNameRenderer } from "./ColumnTypes";
import { ReportFilter, ReportFilterOperator, ReportFilterState } from "./ReportFilterState";
import { ReportColumnDef, ReportColumnFilterValueType } from "./SuggestedReports";

interface ReportFilterTagListProps {
    value: ReportFilterState;
    onChange: (value: ReportFilterState, removedKey: keyof ReportFilterState) => void;
    colDefs: ReportColumnDef[];
    readOnlyFields?: Array<keyof ReportFilterState>;
    hiddenFields?: Array<keyof ReportFilterState>;
}

export const ReportFilterTagList = ({
    value,
    onChange,
    colDefs,
    readOnlyFields = [],
    hiddenFields = [],
}: ReportFilterTagListProps) => {
    const handleCloseClick = (key: keyof ReportFilterState) => () => {
        onChange(omit(value, key) as ReportFilterState, key);
    };

    // By default, status filter is hidden, but if status filters are selected, it should be visible
    const defaultStatusFilters = [
        PurchaseItemStatus.CONFIRMED,
        PurchaseItemStatus.DECLINED,
        PurchaseItemStatus.ABANDONED,
        PurchaseItemStatus.CANCELED,
        PurchaseItemStatus.PENDING_ACCEPT,
        PurchaseItemStatus.PENDING_MINIMUM,
    ];

    if (isEqual(defaultStatusFilters, value?.status?.value && !hiddenFields.includes("status"))) {
        hiddenFields.push("status");
    }

    const visibleFilters = omit(value, hiddenFields);

    return (
        <div className="flex flex-wrap items-center gap-2 py-2 px-6">
            <span className="font-bold">Filters:</span>

            {map(visibleFilters, (filter: ReportFilter, field) => (
                <ReportFilterTag
                    key={field}
                    field={field}
                    filter={filter}
                    colDef={colDefs.find((colDef) => colDef.field === field)}
                    onClose={readOnlyFields.includes(field) ? undefined : handleCloseClick(field)}
                />
            ))}
        </div>
    );
};

interface ReportFilterTagProps {
    field: string;
    filter: ReportFilter;
    colDef?: ReportColumnDef;
    onClose?: () => void;
}

const ReportFilterTag = ({ field, filter, colDef, onClose }: ReportFilterTagProps) => {
    const isConstRange = filter.operator === ReportFilterOperator.RANGE && filter?.rangeName;

    return (
        <Tag key={field} onClose={onClose}>
            <Label field={field} colDef={colDef} />
            <span className="mx-1">{isConstRange ? ReportFilterOperator.IN : OperatorCopy[filter.operator]}</span>
            <Value field={field} filter={filter} colDef={colDef} />
        </Tag>
    );
};

interface LabelProps {
    field: string;
    colDef?: ReportColumnDef;
}

const Label = ({ field, colDef }: LabelProps) => {
    return <span className="font-bold">{colDef?.headerName ?? startCase(field)}</span>;
};

interface ValueProps {
    field: string;
    filter: ReportFilter;
    colDef?: ReportColumnDef;
}

const Value = ({ field, filter, colDef }: ValueProps) => {
    const seller = useSeller();

    if (colDef?.type === "arrivalTime") {
        return formatTime(filter.value, DateFormat.TIME);
    }

    if (filter.operator !== ReportFilterOperator.PERIOD && Array.isArray(filter.value)) {
        if (filter.operator === ReportFilterOperator.RANGE) {
            const rangeLabel = getRangeLabel(filter?.rangeName ?? "");

            return (
                rangeLabel ?? (
                    <Join separator={<span className="mx-1">-</span>}>
                        {filter.value.map((value) => (
                            <Value key={field} field={field} filter={{ ...filter, value }} colDef={colDef} />
                        ))}
                    </Join>
                )
            );
        }

        return <MultiSelectValue field={field} filter={filter} colDef={colDef} />;
    }

    if (filter.operator === ReportFilterOperator.PERIOD && Array.isArray(filter.value)) {
        const [period, relativeStartDate] = filter.value as string[]; // For period, it will always be an array of strings
        const range = getRelativeDateRange(period, relativeStartDate);
        return <>{[formatDate(range.from, DateFormat.DATE), formatDate(range.to, DateFormat.DATE)].join(" - ")}</>;
    }

    if (colDef?.filterValueType === ReportColumnFilterValueType.TIMESTAMP) {
        return <>{formatDate(dayjs(filter.value as number, "X"), DateFormat.DATE)}</>;
    }

    if (colDef?.filterValueType === ReportColumnFilterValueType.DATE) {
        return <>{formatDate(filter.value as string, DateFormat.DATE_LONG)}</>;
    }

    if (filter.operator === ReportFilterOperator.EXISTS) {
        return null;
    }

    if (colDef?.field === "experience") {
        return <ExperienceNameRenderer value={filter.value} />;
    }

    if (colDef?.cellRenderer) {
        const Component = colDef?.cellRenderer;
        return <Component value={filter.value} context={{ seller, target: "tag" }} />;
    }

    if ((Array.isArray(colDef?.type) && colDef?.type.includes("objectid")) || colDef?.type === "objectid") {
        return <span>{filter.value as string}</span>;
    }

    if (colDef?.filterValueType === ReportColumnFilterValueType.STRING) {
        return <span>{filter.value as string}</span>;
    }

    return <>{startCase(filter.value as string)}</>;
};

const MultiSelectValue = ({ field, filter, colDef }: ValueProps) => {
    if (!Array.isArray(filter.value) || filter.value.length === 0) return null;

    const tooltipContent = (
        <div className="flex flex-col gap-0.5">
            {filter.value.map((value) => (
                <span key={`${field}-${value}`}>
                    <Value field={field} filter={{ ...filter, value }} colDef={colDef} />
                </span>
            ))}
        </div>
    );

    return (
        <Tooltip allowHTML content={tooltipContent}>
            <span className="block max-w-[350px] truncate">
                <Join separator={<span className="mr-1">,</span>}>
                    {filter.value.map((value) => (
                        /** For multiselect values, we only need to show first {VISIBLE_VALUES} as comma seperated and rest as "and X more"  */
                        <Value key={field} field={field} filter={{ ...filter, value }} colDef={colDef} />
                    ))}
                </Join>
            </span>
        </Tooltip>
    );
};

const OperatorCopy: Record<ReportFilterOperator, string> = {
    [ReportFilterOperator.EQ]: "is",
    [ReportFilterOperator.NE]: "is not",
    [ReportFilterOperator.GT]: ">",
    [ReportFilterOperator.GTE]: ">=",
    [ReportFilterOperator.LT]: "<",
    [ReportFilterOperator.LTE]: "<=",
    [ReportFilterOperator.IN]: "is",
    [ReportFilterOperator.NIN]: "is not in",
    [ReportFilterOperator.RANGE]: "is between",
    [ReportFilterOperator.PERIOD]: "is between", //  Used for dynamic date ranges
    [ReportFilterOperator.STARTS_WITH]: "starts with",
    [ReportFilterOperator.ENDS_WITH]: "ends with",
    [ReportFilterOperator.CONTAINS]: "contains",
    [ReportFilterOperator.DOES_NOT_CONTAIN]: "does not contain",
    [ReportFilterOperator.EXISTS]: "exists",
};

const getRangeLabel = (rangeOption: string): string => {
    return rangeLabels[rangeOption] ?? undefined;
};
